import React from "react";
import { Button } from "@ndd/ui";

// Which read failures replace the Availability screen (NO-878), and the inline
// notice for the ones that do not.

export type ReadFailureMode = "none" | "replace" | "inline";

/** A failed read replaces the screen only when there is nothing to show; a
 * failed background refetch keeps the editor (and its draft) mounted. */
export function readFailureMode(read: {
  isError: boolean;
  hasData: boolean;
}): ReadFailureMode {
  if (!read.isError) return "none";

  return read.hasData ? "inline" : "replace";
}

/** The inline notice for a refetch that failed while the editor stays up. */
export function RefetchNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-reading"
    >
      <span>We couldn't refresh your hours. Your changes are still here.</span>
      <Button type="button" variant="utility" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/** Which inline notice shows over the editor. A save that went through but
 * whose follow-up read failed outranks the generic refetch notice, so the
 * screen never says "Saved" over hours it could not reload. */
export type InlineNotice = "reload" | "refetch" | null;

export function inlineNotice(state: {
  reloadFailed: boolean;
  readMode: ReadFailureMode;
}): InlineNotice {
  if (state.reloadFailed) return "reload";

  return state.readMode === "inline" ? "refetch" : null;
}

/** Nothing may be edited while a save is in flight or its answer is still
 * being read back: that answer rebuilds the draft and would drop an edit. */
export function isEditingLocked(pending: boolean, resolving: boolean): boolean {
  return pending || resolving;
}

/** The save succeeded but the hours could not be read back afterwards. */
export function ReloadFailedNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-reading"
    >
      <span>Your hours were saved, but we couldn't reload them.</span>
      <Button type="button" variant="utility" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}
